import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { API_BASE_URL } from "../../../api";
import {
  NEARBY_RECONNECT_DELAY_MS,
  NEARBY_SOCKET_EVENTS,
  PAIR_REQUEST_TIMEOUT_MS,
} from "../nearbyTransfer.constants";
import {
  NEARBY_TRANSFER_ACTIONS,
  initialNearbyTransferState,
  nearbyTransferReducer,
} from "../nearbyTransfer.reducer";
import { createNearbyId, getOrCreateNearbyDeviceIdentity } from "../utils/deviceIdentity";
import {
  createPairAcceptMessage,
  createPairDeclineMessage,
  createPairRequestMessage,
  createPresenceJoinMessage,
  parseNearbySocketMessage,
} from "../utils/messages";
import { buildNearbyWebSocketUrl } from "../utils/webSocketUrl";
import { buildNearbyErrorMessage } from "../utils/errorMessage";
import { useNearbyPeerSession } from "./useNearbyPeerSession";

const filterNearbyDevices = ({ devices = [], ownDeviceId }) =>
  devices.filter((device) => device.deviceId !== ownDeviceId);

const buildRemoteDevice = ({ deviceId, deviceName }) => ({
  deviceId,
  deviceName: deviceName || "Nearby Netpad",
});

export const useNearbyTransferSession = ({ isEnabled, transferPayload }) => {
  const [deviceIdentity] = useState(() => getOrCreateNearbyDeviceIdentity());
  const [state, dispatch] = useReducer(nearbyTransferReducer, initialNearbyTransferState);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pendingPairRequestRef = useRef(null);
  const incomingPairRequestRef = useRef(null);
  const pairTimeoutRef = useRef(null);
  const stateRef = useRef(initialNearbyTransferState);
  const wsUrl = useMemo(
    () =>
      buildNearbyWebSocketUrl({
        apiBaseUrl: API_BASE_URL,
        configuredUrl: process.env.REACT_APP_NEARBY_NETPADS_WS_URL,
      }),
    []
  );

  const sendSocketMessage = useCallback((message) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return false;

    socketRef.current.send(JSON.stringify(message));
    return true;
  }, []);

  const clearPairTimeout = useCallback(() => {
    clearTimeout(pairTimeoutRef.current);
    pairTimeoutRef.current = null;
  }, []);

  const clearPairRefs = useCallback(() => {
    pendingPairRequestRef.current = null;
    incomingPairRequestRef.current = null;
    clearPairTimeout();
  }, [clearPairTimeout]);

  const handleFailure = useCallback((errorMessage) => {
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.FAIL, errorMessage });
  }, []);

  const peerSession = useNearbyPeerSession({ dispatch, onFailure: handleFailure, sendSocketMessage, transferPayload });

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const fail = useCallback(
    (errorMessage) => {
      clearPairRefs();
      peerSession.closePeerSession();
      handleFailure(errorMessage);
    },
    [clearPairRefs, handleFailure, peerSession]
  );

  const startPairTimeout = useCallback(() => {
    clearPairTimeout();
    pairTimeoutRef.current = setTimeout(() => {
      clearPairRefs();
      peerSession.closePeerSession();
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.DISCONNECT });
    }, PAIR_REQUEST_TIMEOUT_MS);
  }, [clearPairRefs, clearPairTimeout, peerSession]);

  const handleDevicesMessage = useCallback(
    (message) => {
      dispatch({
        type: NEARBY_TRANSFER_ACTIONS.SET_DEVICES,
        devices: filterNearbyDevices({
          devices: message.devices,
          ownDeviceId: deviceIdentity.deviceId,
        }),
      });
    },
    [deviceIdentity.deviceId]
  );

  const handleIncomingPair = useCallback((message) => {
    if (stateRef.current.isChannelConnected || pendingPairRequestRef.current) return;

    incomingPairRequestRef.current = message;
    startPairTimeout();
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.INCOMING_PAIR, pairRequest: message });
  }, [startPairTimeout]);

  const createAcceptedPairRequest = useCallback((message) => ({
    requestId: message.requestId,
    sessionId: message.sessionId,
    targetDeviceId: message.targetDeviceId,
    device: buildRemoteDevice({
      deviceId: message.targetDeviceId,
      deviceName: message.targetDeviceName,
    }),
  }), []);

  const handlePairAccepted = useCallback(
    async (message) => {
      const isOutgoingPair =
        pendingPairRequestRef.current?.requestId === message.requestId;
      const isIncomingPair =
        incomingPairRequestRef.current?.requestId === message.requestId;
      if (!isOutgoingPair && !isIncomingPair) return;

      const pairRequest = createAcceptedPairRequest(message);
      clearPairRefs();
      peerSession.setPairRequest(pairRequest);
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.PAIRING, connectedDevice: pairRequest.device });

      if (isOutgoingPair) await peerSession.startOffer();
    },
    [clearPairRefs, createAcceptedPairRequest, peerSession]
  );

  const handlePairDeclined = useCallback((message) => {
    const isPendingRequest =
      pendingPairRequestRef.current?.requestId === message.requestId ||
      incomingPairRequestRef.current?.requestId === message.requestId;
    if (!isPendingRequest) return;

    fail("Pair request was declined.");
  }, [fail]);

  const handleServerMessage = useCallback(
    async (messageText) => {
      const message = parseNearbySocketMessage(messageText);
      if (!message) return;

      if (message.type === NEARBY_SOCKET_EVENTS.NEARBY_DEVICES) {
        handleDevicesMessage(message);
      }
      if (message.type === NEARBY_SOCKET_EVENTS.PAIR_INCOMING) {
        handleIncomingPair(message);
      }
      if (message.type === NEARBY_SOCKET_EVENTS.PAIR_ACCEPTED) {
        await handlePairAccepted(message);
      }
      if (message.type === NEARBY_SOCKET_EVENTS.PAIR_DECLINED) {
        handlePairDeclined(message);
      }
      if (message.type === NEARBY_SOCKET_EVENTS.WEBRTC_SIGNAL) {
        await peerSession.receiveSignal(message);
      }
      if (message.type === NEARBY_SOCKET_EVENTS.NEARBY_ERROR) {
        fail(message.message);
      }
    },
    [fail, handleDevicesMessage, handleIncomingPair, handlePairAccepted, handlePairDeclined, peerSession]
  );

  useEffect(() => {
    let isActive = true;

    const connectSocket = () => {
      if (!isEnabled) return;

      dispatch({ type: NEARBY_TRANSFER_ACTIONS.CONNECTING });
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      socket.onopen = () => {
        sendSocketMessage(createPresenceJoinMessage({ deviceIdentity }));
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SOCKET_OPEN });
      };
      socket.onmessage = (event) => {
        handleServerMessage(event.data).catch((error) =>
          fail(buildNearbyErrorMessage({ action: "Handling nearby message", error }))
        );
      };
      socket.onclose = () => {
        clearPairRefs();
        peerSession.closePeerSession();
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SOCKET_CLOSED });
        if (!isActive) return;

        reconnectTimerRef.current = setTimeout(
          connectSocket,
          NEARBY_RECONNECT_DELAY_MS
        );
      };
      socket.onerror = () => socket.close();
    };

    if (isEnabled) connectSocket();
    if (!isEnabled) dispatch({ type: NEARBY_TRANSFER_ACTIONS.SOCKET_CLOSED });

    return () => {
      isActive = false;
      clearTimeout(reconnectTimerRef.current);
      clearPairRefs();
      socketRef.current?.close();
      peerSession.closePeerSession();
    };
  }, [clearPairRefs, deviceIdentity, fail, handleServerMessage, isEnabled, peerSession, sendSocketMessage, wsUrl]);

  const requestPair = useCallback(
    (device) => {
      const requestId = createNearbyId();
      const pairRequest = {
        requestId,
        targetDeviceId: device.deviceId,
        device,
      };
      pendingPairRequestRef.current = pairRequest;
      if (!sendSocketMessage(createPairRequestMessage(pairRequest))) {
        fail("Nearby Netpads is not connected yet.");
        return;
      }

      startPairTimeout();
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.REQUEST_PAIR, pairRequest });
    },
    [fail, sendSocketMessage, startPairTimeout]
  );

  const acceptPair = useCallback(() => {
    const pairRequest = state.incomingPairRequest;
    if (!pairRequest) return;

    const device = buildRemoteDevice({
      deviceId: pairRequest.fromDeviceId,
      deviceName: pairRequest.fromDeviceName,
    });
    if (!sendSocketMessage(createPairAcceptMessage(pairRequest))) {
      fail("Nearby Netpads is not connected yet.");
      return;
    }

    dispatch({ type: NEARBY_TRANSFER_ACTIONS.PAIRING, connectedDevice: device });
  }, [fail, sendSocketMessage, state.incomingPairRequest]);

  const declinePair = useCallback(() => {
    const pairRequest = state.incomingPairRequest;
    if (!pairRequest) return;

    sendSocketMessage(createPairDeclineMessage(pairRequest));
    clearPairRefs();
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.DISCONNECT });
  }, [clearPairRefs, sendSocketMessage, state.incomingPairRequest]);

  const disconnect = useCallback(() => {
    clearPairRefs();
    peerSession.closePeerSession();
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.DISCONNECT });
  }, [clearPairRefs, peerSession]);

  const clearReceivedTransfer = useCallback(() => {
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.CLEAR_RECEIVED_TRANSFER });
  }, []);

  return {
    deviceIdentity,
    state,
    actions: {
      acceptPair,
      clearReceivedTransfer,
      declinePair,
      disconnect,
      requestPair,
      sendCurrentTransfer: peerSession.sendCurrentTransfer,
    },
  };
};
